import { useState, useEffect, useMemo } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, Columns3, CalendarDays, ListChecks, Settings as SettingsIcon, LogOut, Search, HardHat, ScrollText, Layers, Truck, MoreHorizontal, DownloadCloud, Boxes, MapPin, Images, Tag, Ship, Ruler, PackageCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { initials } from "@/lib/format";
import { cn } from "@/lib/utils";
import client from "@/lib/api";
import GlobalSearch from "@/components/GlobalSearch";

const LOGO = "/tilesmate-logo.png";

const NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, testid: "nav-home", end: true },
  { to: "/pipeline", label: "Pipeline", icon: Columns3, testid: "nav-pipeline" },
  { to: "/builders", label: "Builders", icon: HardHat, testid: "nav-builders" },
  { to: "/portfolio", label: "Portfolio", icon: Images, testid: "nav-portfolio" },
  { to: "/po-import", label: "Import PO", icon: DownloadCloud, testid: "nav-po-import" },
  { to: "/flooring", label: "Flooring", icon: Layers, testid: "nav-flooring" },
  { to: "/dispatch", label: "Dispatch", icon: Truck, testid: "nav-dispatch" },
  { to: "/deliveries", label: "Deliveries", icon: PackageCheck, testid: "nav-deliveries" },
  { to: "/stock", label: "Stock", icon: Boxes, testid: "nav-stock" },
  { to: "/stock-locations", label: "Locate Stock", icon: MapPin, testid: "nav-stock-locations" },
  { to: "/price-check", label: "Price Checker", icon: Tag, testid: "nav-price-check" },
  { to: "/takeoffs", label: "Takeoffs", icon: Ruler, testid: "nav-takeoffs" },
  { to: "/followups", label: "Follow-ups", icon: ListChecks, testid: "nav-followups", badge: "overdue" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

function BrandMark({ full }) {
  return (
    <img
      src={LOGO}
      alt="TilesMate — Elevating Spaces"
      data-testid="brand-logo"
      className={full ? "w-full h-auto object-contain" : "h-8 w-auto"}
    />
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const onInstalled = () => setInstallPrompt(null);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const doInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const dash = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await client.get("/dashboard")).data, staleTime: 30_000, enabled: user?.role !== "estimator" && user?.role !== "warehouse" });
  const overdue = dash.data?.overdue || 0;

  const doLogout = async () => { await logout(); navigate("/"); };

  const badgeFor = (item) => (item.badge === "overdue" && overdue > 0 ? overdue : null);

  const isWarehouse = user?.role === "warehouse";
  const isEstimator = user?.role === "estimator";
  const nav = (() => {
    if (isEstimator) return [
      { to: "/takeoffs", label: "My Takeoffs", icon: Ruler, testid: "nav-takeoffs" },
    ];
    if (isWarehouse) return [
      { to: "/dispatch", label: "Dispatch", icon: Truck, testid: "nav-dispatch" },
    ];
    const items = [...NAV];
    if (user?.role === "admin") items.splice(items.length - 1, 0, { to: "/vessels", label: "Vessels", icon: Ship, testid: "nav-vessels" }, { to: "/audit-log", label: "Audit", icon: ScrollText, testid: "nav-audit" });
    return items;
  })();

  const PRIMARY_ROUTES = ["/", "/pipeline", "/builders", "/dispatch"];
  const simpleShell = isWarehouse || isEstimator;
  const primaryNav = simpleShell ? nav : nav.filter((n) => PRIMARY_ROUTES.includes(n.to));
  const moreNav = simpleShell ? [] : nav.filter((n) => !PRIMARY_ROUTES.includes(n.to));
  const moreActive = moreNav.some((n) => location.pathname.startsWith(n.to));
  const showMore = moreNav.length > 0;
  const bottomCols = primaryNav.length + (showMore ? 1 : 0);

  return (
    <div className="min-h-screen bg-background">
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 z-30 w-64 flex-col border-r border-border bg-[#FFFFFF]">
        <div className="border-b border-border px-4 py-4"><BrandMark full /></div>
        <div className="px-3 pb-3">
          <button onClick={() => setSearchOpen(true)} data-testid="global-search-btn" className="flex w-full items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted">
            <Search className="h-4 w-4" /> Search
            <kbd className="ml-auto rounded bg-white px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground shadow-sm">⌘K</kbd>
          </button>
          {installPrompt && (
            <button onClick={doInstall} data-testid="install-app-btn" className="mt-2 flex w-full items-center gap-2 rounded-lg border border-primary/30 bg-accent px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-accent/70">
              <DownloadCloud className="h-4 w-4" /> Install app
            </button>
          )}
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              className={({ isActive }) => cn("group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-ink")}>
              {({ isActive }) => (
                <>
                  <n.icon className={cn("h-[18px] w-[18px]", isActive ? "text-[#F5A623]" : "")} strokeWidth={2} />
                  {n.label}
                  {badgeFor(n) && <span className="ml-auto rounded-full bg-[#DC2626]/12 px-1.5 py-0.5 text-[11px] font-semibold text-error" data-testid="nav-overdue-badge">{badgeFor(n)}</span>}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <div className="flex items-center gap-3 rounded-lg px-2 py-2">
            <Avatar className="h-9 w-9"><AvatarImage src={user?.picture} alt={user?.name} /><AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(user?.name)}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-ink" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="truncate text-[11px] text-muted-foreground capitalize">{user?.role} · {user?.pipeline_mode}</div>
            </div>
            <button onClick={doLogout} data-testid="sidebar-logout-btn" className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-error" title="Log out">
              <LogOut className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between border-b border-border bg-[#F8FAFC]/85 px-4 py-3 backdrop-blur-xl">
        <BrandMark />
        <div className="flex items-center gap-2">
          {installPrompt && (
            <button onClick={doInstall} data-testid="install-app-btn-mobile" className="grid h-8 w-8 place-items-center rounded-md text-primary hover:bg-muted" title="Install app"><DownloadCloud className="h-4 w-4" /></button>
          )}
          <button onClick={() => setSearchOpen(true)} data-testid="global-search-btn-mobile" className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-muted"><Search className="h-4 w-4" /></button>
          <Avatar className="h-8 w-8"><AvatarImage src={user?.picture} alt={user?.name} /><AvatarFallback className="bg-accent text-accent-foreground text-[11px] font-semibold">{initials(user?.name)}</AvatarFallback></Avatar>
        </div>
      </header>

      {/* Main content — centered & width-capped */}
      <main className="md:pl-64">
        <div className="mx-auto w-full max-w-6xl px-4 pb-28 pt-5 md:px-8 md:pb-12 md:pt-8"><Outlet /></div>
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="md:hidden fixed inset-x-0 bottom-0 z-30 grid border-t border-border bg-[#F8FAFC]/95 backdrop-blur-xl" style={{ gridTemplateColumns: `repeat(${bottomCols}, minmax(0, 1fr))` }}>
        {primaryNav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`${n.testid}-mobile`}
            className={({ isActive }) => cn("relative flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium whitespace-nowrap transition-colors", isActive ? "text-primary" : "text-muted-foreground")}>
            <n.icon className="h-5 w-5" strokeWidth={2} />
            {n.label}
            {badgeFor(n) && <span className="absolute right-[22%] top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-error px-1 text-[9px] font-bold text-white">{badgeFor(n)}</span>}
          </NavLink>
        ))}
        <button onClick={() => setMoreOpen(true)} data-testid="nav-more-mobile"
          className={cn("relative flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium whitespace-nowrap transition-colors", moreActive ? "text-primary" : "text-muted-foreground", !showMore && "hidden")}>
          <MoreHorizontal className="h-5 w-5" strokeWidth={2} />
          More
        </button>
      </nav>

      {/* Mobile "More" menu */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="md:hidden rounded-t-2xl" data-testid="more-sheet">
          <SheetHeader className="text-left"><SheetTitle className="font-display">Menu</SheetTitle></SheetHeader>
          <div className="grid grid-cols-3 gap-3 py-4">
            {moreNav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMoreOpen(false)} data-testid={`${n.testid}-more`}
                className={({ isActive }) => cn("flex flex-col items-center gap-2 rounded-xl border p-4 text-xs font-medium transition-colors", isActive ? "border-primary bg-accent text-accent-foreground" : "border-border bg-white text-ink hover:bg-muted")}>
                <n.icon className="h-5 w-5" strokeWidth={2} />
                <span className="text-center leading-tight">{n.label}</span>
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-3 border-t border-border pt-4">
            <Avatar className="h-9 w-9"><AvatarImage src={user?.picture} alt={user?.name} /><AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(user?.name)}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-ink">{user?.name}</div>
              <div className="truncate text-[11px] text-muted-foreground capitalize">{user?.role} · {user?.pipeline_mode}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setMoreOpen(false); doLogout(); }} data-testid="more-logout-btn" className="gap-2"><LogOut className="h-4 w-4" />Log out</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
